import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Button, Icon, Spinner } from '@iq/react-components';
import config from '../../../../../../../config';

import {
  exportVisualizationsTemplate,
  uploadVisualizationsExcel,
  setImportErrors,
  getImportErrors,
  getVisualizationsImportedAt,
  requestVisualizationTemplateTypes,
  getVisualizationTemplateTypes,
} from '../../../../../../../bundles/visualizations';
import { getSources, getImportableSourceTypes } from '../../../../../../../bundles/sources';
import Heading from '../../../../../../Heading';
import ConfirmationDialog from '../../../../../../ConfirmationDialog';
import InstructionalModal from '../../../../../../InstructionalModal';
import SimpleModal from '../../../../../../SimpleModal';
import JsonEditor from '../../../../../../JsonEditor';

const exportUiSchema = {
  'ui:order': [
    'templateType',
    'sourceId',
    'transformers',
    'coolingGroups',
    'coreTemperatureLocations',
    'tapChangers',
    'fiberOpticSensors',
    'windings',
    'harmonicSensors',
    'bushings',
    'dcCurrentPerPhase',
  ],
};

const ImportStep = ({ site, onNext }) => {
  const dispatch = useDispatch();
  const variablesImported = useSelector(getVisualizationsImportedAt);
  const importErrors = useSelector(getImportErrors);
  const templateTypes = useSelector(getVisualizationTemplateTypes);
  const sources = useSelector(getSources);
  const importableSourceTypes = useSelector(getImportableSourceTypes);
  const fullScreenReference = useRef(null);

  const [importing, setImporting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [showNoSourcesDialog, setShowNoSourcesDialog] = useState(false);

  const importableSources = useMemo(
    () => (sources || []).filter((s) => importableSourceTypes.includes(s.type)),
    [sources, importableSourceTypes]
  );

  const sourceOptions = useMemo(
    () => importableSources.map((s) => ({ label: s.name, value: s.id })),
    [importableSources]
  );

  const onPremDefaultSourceId = useMemo(
    () =>
      config.onPremise
        ? (importableSources.find((s) => s.type === 'identiq-ts')?.id ?? null)
        : null,
    [importableSources]
  );

  useEffect(() => {
    if (!templateTypes || templateTypes.length === 0) {
      dispatch(requestVisualizationTemplateTypes());
    }
  }, [dispatch, templateTypes]);

  const templateOptions = useMemo(() => {
    const options = [{ label: 'New', value: 'new' }];
    if (templateTypes) {
      for (const t of templateTypes) {
        options.push({ label: t.displayName, value: t.id });
      }
    }
    return options;
  }, [templateTypes]);

  const exportSchema = useMemo(
    () => ({
      type: 'object',
      required: config.onPremise ? ['templateType'] : ['templateType', 'sourceId'],
      properties: {
        templateType: {
          type: 'string',
          title: 'Select visualization template',
          default: 'new',
          oneOf: templateOptions.map((opt) => ({ enum: [opt.value], title: opt.label })),
        },
        ...(config.onPremise
          ? {}
          : {
              sourceId: {
                type: 'string',
                title: 'Select source',
                oneOf: sourceOptions.map((opt) => ({ enum: [opt.value], title: opt.label })),
              },
            }),
      },
      dependencies: {
        templateType: {
          oneOf: [
            {
              properties: {
                templateType: { enum: ['new'] },
              },
            },
            {
              properties: {
                templateType: { not: { enum: ['new'] } },
                // we only handle 3 transformers for now, so we don't need to expose this option for templates
                // transformers: {
                //   type: 'integer',
                //   title: 'Number of transformers',
                //   default: 3,
                //   minimum: 3,
                //   maximum: 6,
                // },
                coolingGroups: {
                  type: 'integer',
                  title: 'Number of cooling groups',
                  default: 6,
                  minimum: 6,
                  maximum: 8,
                },
                coreTemperatureLocations: {
                  type: 'integer',
                  title: 'Core temperature locations',
                  default: 1,
                  minimum: 1,
                  maximum: 4,
                },
                tapChangers: {
                  type: 'integer',
                  title: 'Number of tap changers',
                  default: 2,
                  minimum: 1,
                  maximum: 3,
                },
                fiberOpticSensors: {
                  type: 'integer',
                  title: 'Number of fiber optic sensors',
                  default: 8,
                  minimum: 8,
                  maximum: 16,
                },
                windings: {
                  type: 'integer',
                  title: 'Number of windings',
                  default: 3,
                  minimum: 2,
                  maximum: 3,
                },
                harmonicSensors: {
                  type: 'integer',
                  title: 'Number of harmonic sensors',
                  default: 2,
                  minimum: 1,
                  maximum: 2,
                },
                bushings: {
                  type: 'integer',
                  title: 'Number of bushings',
                  default: 6,
                  minimum: 4,
                  maximum: 6,
                },
                dcCurrentPerPhase: {
                  type: 'boolean',
                  title: 'DC current per phase',
                  default: false,
                },
              },
            },
          ],
        },
      },
    }),
    [templateOptions, sourceOptions, config.onPremise]
  );

  const onDropAccepted = ([file]) => {
    if (importableSources.length === 0) {
      setPendingFile(file);
      setShowNoSourcesDialog(true);
    } else if (importableSources.length === 1) {
      setImporting(true);
      dispatch(uploadVisualizationsExcel(file, site.id, site.org, importableSources[0].id));
    } else {
      setPendingFile(file);
      setShowSourceDialog(true);
    }
  };

  const onDropRejected = () => {
    dispatch(setImportErrors(['Imports should be from a single Excel file.']));
  };

  const { getInputProps, getRootProps } = useDropzone({
    disabled: importing,
    multiple: false,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [] },
    onDropAccepted,
    onDropRejected,
  });

  useEffect(() => {
    if (importing && variablesImported) {
      setImporting(false);
      onNext();
    }
  }, [importing, variablesImported, onNext]);

  useEffect(() => {
    if (importErrors.length > 0) {
      setImporting(false);
      setShowErrors(true);
    } else {
      setShowErrors(false);
    }
  }, [importErrors]);

  const handleExportSubmit = useCallback(
    ({ formData }) => {
      const { templateType, sourceId: formSourceId, ...options } = formData;
      const sourceId = formSourceId ?? onPremDefaultSourceId;
      const sourceName = sources.find((s) => s.id === sourceId)?.name || 'source';
      dispatch(exportVisualizationsTemplate(site, templateType, sourceId, sourceName, options));
      setShowExportModal(false);
    },
    [dispatch, site, sources, onPremDefaultSourceId]
  );

  const handleImportSourceSubmit = useCallback(
    ({ formData }) => {
      if (pendingFile && formData.sourceId) {
        setImporting(true);
        dispatch(uploadVisualizationsExcel(pendingFile, site.id, site.org, formData.sourceId));
      }
      setShowSourceDialog(false);
      setPendingFile(null);
    },
    [dispatch, pendingFile, site]
  );

  const handleCancelExport = useCallback(() => {
    setShowExportModal(false);
  }, []);

  return (
    <>
      <div className="variable-import-step">
        <Heading
          contentLeft={
            <div className="title">Import state sets and visualizations from an Excel template</div>
          }
          large
        />
        <div className="variable-import-step__button-row">
          <Button
            onClick={() => setShowExportModal(true)}
            activity="secondary"
            design="text"
            icon={
              <div className="icon-green">
                <Icon icon="he-docexcel" />
              </div>
            }
          >
            Export signal & visualization template
          </Button>
          <div className="variable-import-step__info-trigger">
            <Icon icon="he-help" />
            Excel info
          </div>
          <div className="variable-import-step__info-tooltip">
            <p>
              The Statesets sheet can be used to create state sets. The second trigger value column
              will only be used when selecting the &quot;Between&quot; trigger condition.
            </p>
            <p>
              The Visualizations sheet can be used to generate visualizations based on the selected
              template, connecting signals to them under Reference Designation.
            </p>
            <p>
              If a signal should be attached to a state set from the first sheet, enter that state
              set under Stateset Name. If a visualization&apos;s thresholds should be set based on
              one of its signals&apos; state set, indicate this by setting Thresholds from Stateset
              to &quot;True&quot;.
            </p>
          </div>
        </div>
        <div
          className="variable-import-step__drop-zone"
          {...getRootProps()}
        >
          <input
            name="files"
            {...getInputProps()}
          />
          {importing ? (
            <Spinner />
          ) : (
            <div>
              <Icon
                icon="he-upload"
                size="m"
              />
              <p>{'Drag & drop signals excel here, or click to select files'}</p>
            </div>
          )}
        </div>
        {/* <Button
          onClick={() => setShowInstruction(true)}
          activity="secondary"
          design="text"
          icon={
            <div className="icon-wiz-color">
              <Icon icon="he-info" />
            </div>
          }
        >
          How to prepare a signals excel
        </Button> */}
        {showInstruction && (
          <InstructionalModal
            fullScreenRef={fullScreenReference}
            onClose={() => setShowInstruction(false)}
            identifier="wiz-var-import"
            title="How to prepare a signals excel"
          />
        )}
      </div>
      {showErrors ? (
        <ConfirmationDialog
          onConfirm={() => dispatch(setImportErrors([]))}
          onCancel={() => dispatch(setImportErrors([]))}
          showCancel={false}
          title="Import errors"
          subtitle="The following errors occurred while attempting import:"
          body={importErrors.map((error) => (
            <p key={`err-${error}`}>{error}</p>
          ))}
          className="setup-wizard-errors"
          scrollable
        />
      ) : null}
      {showExportModal && (
        <SimpleModal
          onClose={handleCancelExport}
          title="Export signal & visualization template"
          className="export-template-modal-wrapper"
        >
          <JsonEditor
            editorOnly
            schema={exportSchema}
            uiSchema={exportUiSchema}
            formData={{ templateType: 'new' }}
            onFormSubmit={handleExportSubmit}
            initialEditMode
            showEditButton={false}
            saveButtonText="Export"
            cancelCallback={handleCancelExport}
            flexEndButtons
          />
        </SimpleModal>
      )}
      {showSourceDialog && (
        <SimpleModal
          onClose={() => {
            setShowSourceDialog(false);
            setPendingFile(null);
          }}
          title="Select source for import"
          className="import-source-modal-wrapper"
        >
          <JsonEditor
            editorOnly
            schema={{
              type: 'object',
              required: ['sourceId'],
              properties: {
                sourceId: {
                  type: 'string',
                  title: 'Select source',
                  oneOf: sourceOptions.map((opt) => ({ enum: [opt.value], title: opt.label })),
                },
              },
            }}
            onFormSubmit={handleImportSourceSubmit}
            initialEditMode
            showEditButton={false}
            saveButtonText="Import"
            cancelCallback={() => {
              setShowSourceDialog(false);
              setPendingFile(null);
            }}
            flexEndButtons
          />
        </SimpleModal>
      )}
      {showNoSourcesDialog && (
        <ConfirmationDialog
          onConfirm={() => {
            setShowNoSourcesDialog(false);
            setPendingFile(null);
          }}
          onCancel={() => {
            setShowNoSourcesDialog(false);
            setPendingFile(null);
          }}
          showCancel={false}
          title="No importable sources found"
          body="Please add and configure a source before importing visualizations."
        />
      )}
    </>
  );
};

export default ImportStep;
